import React from "react";

import { Grid } from "../../UI/Grid";
import { MainLayout } from "../../layout/MainLayout";
import {
  EntrataReminderEmails,
  Network,
  UpdateNotificationsRequest,
  UpdateUserNotifications,
} from "../../services/NetworkApi";
import useAuth from "../login/AuthContext";
import { ErrorResponse } from "../../services/Common";
import { useNetwork } from "../../hooks/useNetwork";
import { useNetworkContext } from "../../contexts/NetworkContext/context";
import { useSelectedNetworkImportTypes } from "../../hooks/useSelectedNetworkImportType";
import { normalize } from "../../helpers/normalize";
import { L2Selector } from "../../common/network/NetworkSelectors";
import { Box } from "../../UI/Box";
import { ConfirmationContainer } from "../../components/ConfirmationContainer";
import { ConfirmationNotificationTable } from "../../components/ConfirmationNotificationTable";
import { EntrataReminderNotificationsTable } from "../../components/EntrataReminderNotificationsTable";
import { useSnackbar } from "../../hooks/useSnackbar";

type ClosingNotifications = {
  set: boolean;
  l2Name: null | string;
  closingDate: null | string;
  update: boolean;
  is10: null | boolean;
  is5: null | boolean;
  is3: null | boolean;
  is1: null | boolean;
};

interface DataState {
  confirmMessage: string;
  confirmNotification: string;
  notificationDays: ClosingNotifications[];
  entrataNotifications: EntrataReminderEmails;
  entrataUpdated: boolean;
  hasEndDate: boolean;
}

function stateToRequest(data: DataState) {
  const request: UpdateNotificationsRequest = {
    matchingText: {
      confirmMessage: data.confirmMessage,
      confirmNotification: data.confirmNotification,
    },
    reminderEmails: [
      {
        daysBefore: 10,
        enabled: data.notificationDays[0].is10 || false,
      },
      {
        daysBefore: 5,
        enabled: data.notificationDays[0].is5 || false,
      },
      {
        daysBefore: 3,
        enabled: data.notificationDays[0].is3 || false,
      },
      {
        daysBefore: 1,
        enabled: data.notificationDays[0].is1 || false,
      },
    ],
    entrataReminderEmails: { ...data.entrataNotifications },
  };
  return request;
}

const CONFIRMATION_APP_MESSAGE_TOOLTIP_TITLE = "Confirmation Message (In-App)";
const CONFIRMATION_APP_MESSAGE_TOOLTIP_CONTENT =
  "This message is shown to your residents in the RoomSync app when a match request is confirmed by both parties.  This message can be customized to include things like next steps or community contact information.";
const CONFIRMATION_APP_MESSAGE_TOOLTIP_TITLE_PDF = {
  link: "/static/pdf/confirmation_message.png",
  text: "View an example of this email communication"
};

const CONFIRMATION_EMAIL_MESSAGE_TOOLTIP_TITLE = "Confirmation Message (Email)";
const CONFIRMATION_EMAIL_MESSAGE_TOOLTIP_CONTENT =
  "This message is emailed to your residents when a match request is confirmed by both parties.  This message can be customized to include things like next steps or community contact information.";
const CONFIRMATION_EMAIL_MESSAGE_TOOLTIP_CONTENT_PDF = {
  link: "/static/pdf/confirmation_email.png",
  text: "View an example of this email communication"
};

export const UserNotificationsPage = () => {
  const defaultNotification = [
    {
      set: false,
      l2Name: null,
      closingDate: null,
      update: false,
      is10: null,
      is5: null,
      is3: null,
      is1: null,
    },
  ];

  const defaultEntrata: EntrataReminderEmails = {
    enabled: false,
    tenDayReminder: false,
    threeDayReminder: false,
    twentyOneDayReminder: false,
  };

  const [subnetwork, setSubnetwork] = React.useState<number>();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const { getAuthToken } = useAuth();
  const { getL2, selectedNetwork } = useNetwork();
  const { networks, loadNetworkInfoForNetworks } = useNetworkContext();
  const { isEntrata, isAcc, isRms, isKinetic, isEmail } = useSelectedNetworkImportTypes();

  const [data, setData] = React.useState<DataState>({
    confirmMessage: "",
    confirmNotification: "",
    notificationDays: defaultNotification,
    entrataNotifications: defaultEntrata,
    hasEndDate: false,
    entrataUpdated: false,
  });

  const l2 = React.useMemo(() => subnetwork && getL2(selectedNetwork.l1Id, subnetwork), [
    selectedNetwork.l1Id,
    subnetwork,
    getL2,
  ]);

  const isReadOnly = React.useMemo(() =>  !!((l2 && (l2?.matchingRoundStatus === "ARCHIVED" || l2?.matchingRoundStatus === "CLOSED")) ), [l2]);

  React.useEffect(() => {
    if (!selectedNetwork || !subnetwork) {
      return;
    }

    const l2 = getL2(selectedNetwork.l1Id, subnetwork);

    if (!l2) {
      return;
    }

    const { matchingText } = l2;
    const { reminderEmails } = l2;

    const loadedData = {
      ...data,
    };

    if (matchingText) {
      loadedData.confirmMessage = matchingText.confirmMessage;
      loadedData.confirmNotification = matchingText.confirmNotification;
    } else {
      loadedData.confirmMessage = "";
      loadedData.confirmNotification = "";
    }

    if (reminderEmails) {
      let dateString = "No Close Date";
      if (l2.matchingEndDate) {
        const [year, month, day] = l2.matchingEndDate.split("-");
        dateString = `${month}/${day}/${year}`;
        loadedData.hasEndDate = true;
      }

      loadedData.notificationDays = [
        {
          set: true,
          l2Name: l2.l2Name,
          update: false,
          closingDate: dateString,
          is10: reminderEmails.filter((x: any) => x.daysBefore === 10)[0]
            .enabled,
          is5: reminderEmails.filter((x: any) => x.daysBefore === 5)[0].enabled,
          is3: reminderEmails.filter((x: any) => x.daysBefore === 3)[0].enabled,
          is1: reminderEmails.filter((x: any) => x.daysBefore === 1)[0].enabled,
        },
      ];
    } else {
      loadedData.notificationDays = defaultNotification;
      loadedData.hasEndDate = false;
    }

    const c = networks.filter(
      (x: Network) => x.l1Id == selectedNetwork.l1Id && x.l2Id == subnetwork
    )[0];
    if (c.entrataReminderEmails) {
      loadedData.entrataNotifications = { ...c.entrataReminderEmails };
    } else {
      loadedData.entrataNotifications = {
        enabled: false,
        twentyOneDayReminder: false,
        tenDayReminder: false,
        threeDayReminder: false,
      };
    }

    setData(loadedData);
  }, [selectedNetwork.l1Id, subnetwork, normalize(networks)]);

  React.useEffect(() => {
    if (
      (data.notificationDays[0].set && data.notificationDays[0].update) ||
      data.entrataUpdated
    ) {
      const handler = setTimeout(() => {
        async function sendRequest() {
          const currentNetwork = selectedNetwork;

          if (!currentNetwork || !subnetwork) {
            return;
          }

          const request = stateToRequest(data);

          const res = await UpdateUserNotifications(
            currentNetwork.l1Id,
            subnetwork,
            request,
            await getAuthToken()
          );

          if (res.status !== 200) {
            showErrorSnackbar();
          } else {
            await loadNetworkInfoForNetworks([currentNetwork.l1Id]);
            showSuccessSnackbar("Update successful");
          }

          setData({ ...data, entrataUpdated: false });
        }

        sendRequest();
      }, 500);
      return () => clearTimeout(handler);
    }
  }, [
    data.notificationDays[0].is1,
    data.notificationDays[0].is3,
    data.notificationDays[0].is5,
    data.notificationDays[0].is10,
    data.entrataNotifications.enabled,
    data.entrataNotifications.threeDayReminder,
    data.entrataNotifications.tenDayReminder,
    data.entrataNotifications.twentyOneDayReminder,
  ]);

  function updateNotificationDate(field: string, prevState: any) {
    const updatedValue = !prevState.notificationDays[0][`is${field}`];

    const updatedValues = { ...prevState };

    updatedValues.notificationDays[0][`is${field}`] = updatedValue;
    updatedValues.notificationDays[0].update = true;

    return updatedValues;
  }

  const updateFunction = (field: string) => async (text: string) => {
    const updatedData = { ...data, [field]: text };

    if (!selectedNetwork || !subnetwork) {
      return;
    }

    const req = stateToRequest(updatedData);

    const update = await UpdateUserNotifications(
      selectedNetwork.l1Id,
      subnetwork,
      req,
      await getAuthToken()
    );

    if (update instanceof ErrorResponse) {
      showErrorSnackbar(update.message);
    } else {
      showSuccessSnackbar("Update successful");
    }

    setData(updatedData);
    await loadNetworkInfoForNetworks([selectedNetwork.l1Id]);
  };

  return (
    <MainLayout title="User Notifications Text">
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box width={280}>
            <L2Selector onSelectHandler={setSubnetwork} />
          </Box>
        </Grid>
        {!!subnetwork && (
          <>
            <Grid item xs={12}>
              <ConfirmationContainer
                text={data.confirmMessage}
                onUpdateClick={updateFunction("confirmMessage")}
                title={CONFIRMATION_APP_MESSAGE_TOOLTIP_TITLE}
                titleTooltipText={CONFIRMATION_APP_MESSAGE_TOOLTIP_CONTENT}
                urlHelpTooltip={CONFIRMATION_APP_MESSAGE_TOOLTIP_TITLE_PDF}
                isDisabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <ConfirmationContainer
                text={data.confirmNotification}
                title={CONFIRMATION_EMAIL_MESSAGE_TOOLTIP_TITLE}
                urlHelpTooltip={CONFIRMATION_EMAIL_MESSAGE_TOOLTIP_CONTENT_PDF}
                onUpdateClick={updateFunction("confirmNotification")}
                titleTooltipText={CONFIRMATION_EMAIL_MESSAGE_TOOLTIP_CONTENT}
                isDisabled={isReadOnly}
              />
            </Grid>
            {(data.hasEndDate || isKinetic) && (
              <Grid item xs={12}>
                <ConfirmationNotificationTable
                  data={data}
                  setData={setData}
                  updateNotificationDate={updateNotificationDate}
                  isDisabled={isReadOnly}
                />
              </Grid>
            )}
            {(isEntrata || isAcc || isRms || isKinetic || isEmail) && (
              <Grid item xs={12}>
                <EntrataReminderNotificationsTable
                  l2Name={data.notificationDays[0].l2Name || ""}
                  setEntrataEmailNotification={(
                    params: EntrataReminderEmails
                  ) => {
                    setData({
                      ...data,
                      entrataNotifications: params,
                      entrataUpdated: true,
                    });
                  }}
                  emailDetails={data.entrataNotifications}
                  isDisabled={isReadOnly}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </MainLayout>
  );
};
